import { useNavigate } from 'react-router-dom';

import { Button } from 'antd';

import { programConfig, programLabels } from '@/config';
import { getManufacturerByProgram } from '@/libs/util';
import useGlobalConfig from '@/store/global';

import CopayAssistance from './CopayAssistance';
import AlygloSupport from './alyglo';
import CoagadexSupport from './coagadex';
import GammakedSupport from './gammaked';
import GammaplexSupport from './gammaplex';
import GamunexSupport from './gamunex';
import KoateSupport from './koate';
import RyplazimSupport from './ryplazim';
import { supportInfo } from './support/infos';
import YimmugoSupport from './yimmugo';

const SUPPORT_COMPONENTS = {
  alyglo: <AlygloSupport />,
  coagadex: <CoagadexSupport />,
  gammaked: <GammakedSupport />,
  gammaplex: <GammaplexSupport />,
  gamunex: <GamunexSupport />,
  koate: <KoateSupport />,
  ryplazim: <RyplazimSupport />,
  wilate: <CopayAssistance name="wilate" />,
  panzyga: <CopayAssistance name="panzyga" />,
  fibryga: <CopayAssistance name="fibryga" />,
  nuwiq: <CopayAssistance name="nuwiq" />,
  cutaquig: <CopayAssistance name="cutaquig" />,
  octagam: <CopayAssistance name="octagam" />,
  yimmugo: <YimmugoSupport />,
};

const ProviderSupport = () => {
  const { program, themeColor } = useGlobalConfig();
  const navigate = useNavigate();
  const name = program.name;
  const medicalProgram = Object.keys(SUPPORT_COMPONENTS);
  const isOctaPharma = getManufacturerByProgram(program.name)?.indexOf('Octapharma') !== -1;
  const info = supportInfo[name as keyof typeof supportInfo];
  const programInfo = programConfig.find((item) => item.Program === program.name);

  const MedicalContent = () => {
    return SUPPORT_COMPONENTS[name as keyof typeof SUPPORT_COMPONENTS] || null;
  };

  if (!medicalProgram.includes(name)) {
    return null;
  }

  return (
    <div className="mx-auto pb-10 text-base">
      <div className="text-xl md:text-3xl font-bold text-[#000000]">
        {!isOctaPharma && (
          <span style={{ color: themeColor }}>{programLabels[name].name}&nbsp;</span>
        )}
        SUPPORT FOR HEALTHCARE PROVIDERS
      </div>

      <MedicalContent />

      <Button
        type="primary"
        onClick={() => navigate('/medical_portal')}
        className="w-full md:w-[40%] mt-10 text-sm md:text-base"
        style={{
          backgroundColor: themeColor,
          borderColor: themeColor,
          height: '36px',
        }}
      >
        {programLabels[name].label} Portal For Providers
      </Button>
      {name === 'yimmugo' && (
        <>
          <div className="mt-10 text-2xl font-bold">ADDITIONAL REIMBURSEMENT SUPPORT</div>
          <p className="my-2">
            For inquiries or assistance with reimbursement, including benefits verification and/or
            authorization, please contact our Support Team at{' '}
            <a
              className="text-blue-500 hover:text-blue-600 hover:underline"
              href={`tel:${programInfo?.SupportTeamPhone}`}
            >
              {programInfo?.SupportTeamPhone}
            </a>
            . We&apos;re here to provide support where needed.
          </p>
        </>
      )}

      {name !== 'gamunex' && !isOctaPharma && name !== 'yimmugo' && (
        <>
          <div className="mt-10 text-2xl font-bold">ADDITIONAL REIMBURSEMENT SUPPORT</div>

          <p className="my-2">
            For any inquiries or assistance with reimbursement, please don't hesitate to contact our
            dedicated Support Team at{' '}
            <a
              className="text-blue-500 hover:text-blue-600 hover:underline"
              href={`tel:${programInfo?.SupportTeamPhone}`}
            >
              {programInfo?.SupportTeamPhone}
            </a>
            . We are fully committed to guiding you through any questions or concerns you may
            encounter.
          </p>

          <p className="my-2">
            Please reach out to our Reimbursement Support Team at{' '}
            <a
              className="text-blue-500 hover:text-blue-600 hover:underline"
              href={`tel:${programInfo?.SupportTeamPhone}`}
            >
              {programInfo?.SupportTeamPhone}
            </a>{' '}
            if your patient:
          </p>

          <ul className="my-2 list-disc ml-6">
            <li>Requires benefit verification</li>
            <li>Requires prior authorization</li>
          </ul>

          <p className="my-2">
            We're here to support you every step of the way and eagerly anticipate the opportunity
            to assist you.
          </p>
        </>
      )}

      {isOctaPharma && (
        <>
          <div className="provider-support mt-10 ">
            <div className="text-2xl font-bold">{info.title}</div>
            <p className="mt-6">{info.description}</p>

            {'supportItems' in info && info.supportItems && (
              <ul className="my-2 list-disc ml-6">
                {info.supportItems.map((item, index) => (
                  <li className="my-2" key={index}>
                    {item.label}
                  </li>
                ))}
              </ul>
            )}

            {'freeTrial' in info && info.freeTrial && (
              <div className="free-trial mt-4">
                <h3 className="font-bold">{info.freeTrial.title}</h3>
                {'description' in info.freeTrial && (
                  <p className="my-2">{info.freeTrial.description}</p>
                )}
                <ul className="list-disc ml-6">
                  {info.freeTrial.details.map((detail, index) => (
                    <li className="my-2" key={index}>
                      {detail.content}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {'footer' in info && info.footer && <p className="mt-6">{info.footer}</p>}
          </div>
        </>
      )}

      {name === 'yimmugo' && (
        <div className="mt-8">
          This program is subject to change or discontinuation by&nbsp;
          <b>{getManufacturerByProgram(name)}</b>
          &nbsp;at any time, without notice, for any reason. This program is administered by&nbsp;
          <a
            target="_blank"
            href="https://medmonk.com/"
            className="text-blue-500 hover:text-blue-600 hover:underline"
          >
            Medmonk
          </a>
          , on the behalf of&nbsp;
          <b>{getManufacturerByProgram(name)}</b>
          &nbsp;for the {programLabels[name].label} Program.
        </div>
      )}

      {name !== 'yimmugo' && (
        <div className="mt-8">
          This program is subject to change or discontinuation by&nbsp;
          <b>{getManufacturerByProgram(name)}</b>
          &nbsp;at any time, for any reason, and with or without prior notice. This portal is
          administered by&nbsp;
          <a
            target="_blank"
            href="https://medmonk.com/"
            className="text-blue-500 hover:text-blue-600 hover:underline"
          >
            Medmonk
          </a>
          &nbsp;for the {programLabels[name].label} Program.
        </div>
      )}

      {name === 'yimmugo' && (
        <div className="mt-8">
          <div className="mt-10 text-2xl font-bold">Terms and Conditions</div>
          <p className="mt-2">
            Kedrion Biopharma’s YIMMUGO Copay Assistance Program (the “Program”) provides financial
            support for patients who meet all the eligibility criteria.  Participation in the
            Program and provision of financial support is subject to these Program Terms and
            Conditions, including the $8,000 yearly maximum (“Yearly Program Maximum”).  By
            enrolling in the Program, you agree that the Program is intended solely for the benefit
            of you, the patient, not health plans and/or their partners, and that you will comply
            with all applicable requirements of your health plan.  The Program is not valid for
            patients that pay cash, are currently in a coverage gap, insurance pays the entire cost
            of their prescription, or are a beneficiary of, or any part of their prescription is
            covered by: any federal, state, or government-funded health program (Medicare, Medicare
            Advantage, Medicare Part D, Medicaid, Medigap, TRICARE®, Veterans Affairs (VA), the
            Department of Defense (DoD), or the Government Health Insurance Plan available in Puerto
            Rico).  No claim for reimbursement of the out-of-pocket expense amount covered by the
            Program shall be submitted to any public or private third-party payer, including
            flexible spending accounts (FSA) or healthcare savings accounts (HSA).  If you are
            enrolled in a co-pay maximizer program, your Yearly Program Maximum may vary over time
            to ensure the program funds are used for your benefit.  Kedrion reserves the right to
            reduce or eliminate the copay assistance available to patients it becomes aware that
            they are enrolled in an insurance plan that utilizes a co-pay maximizer program.  If you
            learn your health plan has implemented a co-pay maximizer program, you agree to notify
            the Program immediately.  The Program only applies in the United States and its
            territories, and does not apply where prohibited by law, taxed, or restricted. Coverage
            of certain administration charges will not apply for patients residing in states where
            it is prohibited by law.  This does not constitute health insurance.  Void where use is
            prohibited by your insurance provider.  You agree to notify the Program immediately if
            your insurance situation changes.  This Program is not transferable and is limited to
            one per patient and may not be combined with any other coupon, discount, prescription
            savings card, rebate, free trial, patient assistance, alternative funding program,
            co-pay accumulator, or other offer, including those from third parties and companies
            that help insurers or health plans manage costs. Not valid if reproduced.  Any
            individual or entity who enrolls or assists in the enrollment of a patient in the
            Program represents that the patient meets the eligibility criteria and other
            requirements described herein. Additional terms and conditions may apply.  Data related
            to your redemption of the Program may be collected, analyzed, and shared with Kedrion
            Biopharma for market research and other purposes related to assessing its programs. 
            Data shared with Kedrion Biopharma will be aggregated and de-identified.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProviderSupport;
